import appConfig from '@/configs/app.config';
import { REDIRECT_URL_KEY } from '@/constants/app.constant';
import { handleGetFirebaseToken, handleRemoveFirebaseToken } from '@/firebase';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { ForgotPassword, ForgotPassword_Body, ResetPassword, ResetPassword_Body, ResetPassword_Params, SigninUser, Signin_Body, SignupUser, Signup_Body } from '@/services/auth-service';
import { onSignInSuccess, onSignOutSuccess, onStartSigningOut, onStopSigningOut } from '@/store/auth/sessionSlice';
import { initialState, setUser } from '@/store/auth/userSlice';
import { getPagesConfigThunk } from '@/store/page-config/page-config-slice';
import { updateApiPrefix, updateUrlsWithCompany, resetUrlsToOriginal } from '@/configs/config-accessor';
import { useNavigate } from 'react-router-dom';
import { timeout } from '../timerdelay';
import useQuery from './useQuery';

interface ExtendedSignin_Body extends Signin_Body {
  companyName?: string;
}

function useAuth() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const query = useQuery();

  const { id: userId } = useAppSelector((state) => state.auth.user);
  const { token, signedIn } = useAppSelector((state) => state.auth.session);

  const signIn = async (data: ExtendedSignin_Body): Promise<{ status: string; code: number; message: string }> => {
    try {
      const response = await SigninUser(data);
      if (response.data.data && response.data.status === 200) {
        const authData = response.data.data;

        const user = authData.user;
        const password = data.password;

     

        const hashedUser = btoa(user);
        const hashedPassword = btoa(password);
        localStorage.setItem('username', hashedUser);
        localStorage.setItem('security_key', hashedPassword);
        localStorage.setItem('token', authData.token);

        // Store company name and update API prefix and URLs
        if (data.companyName) {
          localStorage.setItem('companyName', btoa(data.companyName));
          // Update the API prefix to include company name
          updateApiPrefix(data.companyName);
          // Update posUrl and orderManagementUrl with company name
          updateUrlsWithCompany(data.companyName);
        }

        await timeout(2000);

        const userDataResponse = {
          data: {
            data: [
              {
                role: {
                  label: 'Super Admin',
                  value: '123',
                },
                image: [
                  {
                    url: 'https://placeholder.com/150',
                  },
                ],
              },
            ],
          },
        };

        const userData = userDataResponse.data.data[0];
        await dispatch(getPagesConfigThunk({ token: authData.token, userRole: userData.role.label }));

        const userRoleResponse = {
          data: [
            {
              authenticatedEntryPath: '/apps',
            },
          ],
        };
        const authenticatedEntryPath = userRoleResponse.data[0].authenticatedEntryPath || '/';
        const id = authData.ID;
        const avatar = userData.image.length > 0 ? userData.image[0].url : '';
        const name = authData.Name;
        const userName = authData.UserName;
        const userRole = userData.role;
        const authority = [userData.role.label];
        const email = authData.email;

        dispatch(onSignInSuccess(authData.token));
        dispatch(setUser({ id, avatar, name, userName, authority, email, userRole }));
        handleGetFirebaseToken(dispatch, id);

        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(redirectUrl ? redirectUrl : authenticatedEntryPath);

        return { status: 'success', code: 0, message: '' };
      } else {
        return { status: 'failed', code: 0, message: response.data.message || 'Something went wrong!' };
      }
    } catch (errors) {
      if (!!errors && !!errors['response']['data']['message']) return { status: 'failed', code: parseInt(errors['response']['data']['status'] || '0'), message: errors['response']['data']['message'] };
      return { status: 'failed', code: 0, message: `${errors}` };
    }
  };

  const signUp = async (data: Signup_Body): Promise<{ status: string; code: number; message: string }> => {
    try {
      const response = await SignupUser(data);
      if (response.data.status === 201) {
        navigate(appConfig.unAuthenticatedEntryPath);
        return { status: 'success', code: 0, message: '' };
      } else {
        return { status: 'failed', code: 0, message: response.data.message || 'Something went wrong!' };
      }
    } catch (errors) {
      if (!!errors && !!errors['response']['data']['message']) return { status: 'failed', code: parseInt(errors['response']['data']['status'] || '0'), message: errors['response']['data']['message'] };
      return { status: 'failed', code: 0, message: `${errors}` };
    }
  };

  const signOut = async () => {
    try {
      dispatch(onStartSigningOut());
      await handleRemoveFirebaseToken(userId);

      localStorage.removeItem('username');
      localStorage.removeItem('security_key');
      localStorage.removeItem('companyName');

      // Reset URLs to original values
      resetUrlsToOriginal();

      dispatch(onStopSigningOut());
      dispatch(onSignOutSuccess());
      dispatch(setUser(initialState));
    } catch (error) {}

    localStorage.clear();
    navigate(appConfig.unAuthenticatedEntryPath);
  };

  const forgotPassword = async (data: ForgotPassword_Body): Promise<{ status: string; code: number; message: string }> => {
    try {
      const response = await ForgotPassword(data);
      if (response.data.status === 200) {
        return { status: 'success', code: 0, message: '' };
      } else {
        return { status: 'failed', code: 0, message: response.data.message || 'Something went wrong!' };
      }
    } catch (errors) {
      if (!!errors && !!errors['response']['data']['message']) return { status: 'failed', code: parseInt(errors['response']['data']['status'] || '0'), message: errors['response']['data']['message'] };
      return { status: 'failed', code: 0, message: `${errors}` };
    }
  };

  const resetPassword = async (data: ResetPassword_Body, params: ResetPassword_Params): Promise<{ status: string; code: number; message: string }> => {
    try {
      const response = await ResetPassword(params, data);
      if (response.data.status === 200) {
        return { status: 'success', code: 0, message: '' };
      } else {
        return { status: 'failed', code: 0, message: response.data.message || 'Something went wrong!' };
      }
    } catch (errors) {
      if (!!errors && !!errors['response']['data']['message']) return { status: 'failed', code: parseInt(errors['response']['data']['status'] || '0'), message: errors['response']['data']['message'] };
      return { status: 'failed', code: 0, message: `${errors}` };
    }
  };

  return {
    authenticated: token && signedIn,
    signIn,
    signUp,
    signOut,
    forgotPassword,
    resetPassword,
  };
}

export default useAuth;
